import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Auth0Client } from "@auth0/auth0-spa-js";
import config from "../../config";
import { initialUserContext } from "./shared";
import { ContextValue } from "./types";
import useTokenUserData from "./useTokenUserData";
import { isPublicPath } from "../../screens/NavigatorScreen/utils";
import { sendRaygunError } from "../useRaygun";
import useImpersonation from "./useImpersonation";

const PGA_TOKEN_NAME = "pgaIDToken";
const DEFAULT_SESSION_CHECK_INTERVAL = 2 * 60 * 1000;

const setSessionToken = (token: string) => sessionStorage.setItem(PGA_TOKEN_NAME, token);
const getSessionToken = () => sessionStorage.getItem(PGA_TOKEN_NAME) || undefined;
const clearSessionToken = () => sessionStorage.removeItem(PGA_TOKEN_NAME);

let auth0Client: Auth0Client | undefined;

if (config.authImpl === "auth0") {
  auth0Client = new Auth0Client({
    domain: config.auth0Domain,
    clientId: config.auth0ClientId,
    authorizationParams: {
      audience: config.auth0Audience,
      redirect_uri: `${window.location.origin}/member`,
    },
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
  });
}

async function fetchSessionToken() {
  if (config.authImpl === "auth0") {
    try {
      const accessToken = await auth0Client!.getTokenSilently();
      return accessToken;
    } catch (error: any) {
      const skippedErrors = ["login_required", "missing_refresh_token", "invalid_grant"];
      if (!skippedErrors.includes(error.error)) {
        throw error;
      }
      return null;
    }
  } else {
    try {
      const response = await fetch(`${config.webAuthBaseUrl}/session`, {
        method: "POST",
        mode: "cors",
        credentials: "include",
      }).then((r) => {
        if (!r.ok) {
          throw new Error(`Session request failed`);
        }
        return r.json();
      });

      const { token } = response;

      if (token) {
        return token as string;
      }
    } catch (err) {
      console.log({ err });
    }
  }
}

export const UserContext = createContext<ContextValue>(initialUserContext);

const redirectLogin = async () => {
  if (config.authImpl === "auth0") {
    await auth0Client!.loginWithRedirect({
      authorizationParams: {
        scope: `openid profile email offline_access`,
      },
      appState: { returnTo: window.location.href },
    });
  } else {
    window.location.assign(`${config.webAuthBaseUrl}/login?return_to=${window.location}`);
  }
};

const redirectLogout = async () => {
  if (config.authImpl === "auth0") {
    await auth0Client!.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/logged-out`,
      },
    });
  } else {
    window.location.assign(`${config.webAuthBaseUrl}/logout?return_to=${window.location.origin}`);
  }
};

function hasRedirectParams() {
  const searchCodes = ["state", "code", "error"];
  return searchCodes.some((code) => window.location.search.includes(code));
}

async function handleRedirectCallback() {
  try {
    const { appState } = await auth0Client!.handleRedirectCallback();
    if (appState?.returnTo) {
      window.location = appState.returnTo;
    }
  } catch (error) {
    console.error("Error handling redirect callback:", error);
    sendRaygunError(new Error(`Error handling redirect callback: ${error}`));
  }
}

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | undefined>(getSessionToken());
  const [loaded, setLoaded] = useState(!!token);

  const { firstName, lastName, oneLoginId, memberNumber } = useTokenUserData(token);

  const { canImpersonate, impersonatedUser, assumeUser, clearAssumedUser } = useImpersonation(token);

  useEffect(() => {
    const load = () => {
      if (isPublicPath()) {
        setLoaded(true);
        return;
      }

      fetchSessionToken().then((newToken) => {
        if (newToken) {
          setSessionToken(newToken);
          setToken(newToken);
          setLoaded(true);
        } else {
          redirectLogin();
        }
      });
    };

    if (hasRedirectParams()) {
      handleRedirectCallback();
    }

    load();

    if (process.env.NODE_ENV != "test") {
      setInterval(() => {
        load();
      }, DEFAULT_SESSION_CHECK_INTERVAL);
    }
  }, []);

  const setCurrentUser = useCallback(() => {
    throw new Error("Can't set current user for web implementation");
  }, []);

  const logout = useCallback(() => {
    clearSessionToken();
    redirectLogout();
  }, []);

  const value = {
    ...initialUserContext,
    token,
    firstName: impersonatedUser?.firstName || firstName,
    lastName: impersonatedUser?.lastName || lastName,
    memberNumber: impersonatedUser?.memberNumber || memberNumber,
    oneLoginId,
    loaded,
    setCurrentUser,
    logout,
    impersonatedUser,
    canImpersonate,
    assumeUser,
    clearAssumedUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const useCurrentUser = () => useContext(UserContext);

export default useCurrentUser;
